import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { HelpCircle, Download, ChevronRight, BookOpen, Loader2, CheckCircle2, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ESSUserGuide() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/ess/user-guide`, { withCredentials: true });
        const list = data?.sections || [];
        setSections(list);
        if (list.length) setActiveId(list[0].id);
      } catch (e) {
        toast.error('Failed to load user guide');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) => {
      const hay = [
        s.title, s.intro, ...(s.steps || []), ...(s.tips || []),
        ...((s.faqs || []).map((f) => `${f.q} ${f.a}`)),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [sections, query]);

  useEffect(() => {
    if (filtered.length > 0 && !filtered.some((s) => s.id === activeId)) {
      setActiveId(filtered[0].id);
    }
  }, [filtered, activeId]);

  const active = useMemo(
    () => sections.find((s) => s.id === activeId) || null,
    [sections, activeId],
  );

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/ess/user-guide/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ESS_User_Guide.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('User guide downloaded');
    } catch (e) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-6" data-testid="ess-user-guide-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              Employee Portal User Guide
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              A comprehensive handbook to help you navigate and use all Employee Portal features.
            </p>
          </div>
          <button
            data-testid="ess-user-guide-download"
            onClick={downloadPdf}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors shadow-sm"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
        </div>

        <div className="mb-4">
          <input
            data-testid="ess-user-guide-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the guide — e.g. 'payslip', 'leave', 'regularization', 'reimbursement', 'advance', 'KYC', 'tax', 'request', 'help', 'appointment', 'documents'"
            className="w-full max-w-xl border border-slate-300 rounded-lg px-4 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading guide contents…</div>
        ) : (
          <div className="grid grid-cols-12 gap-6" data-testid="ess-user-guide-content">
            {/* Sidebar section index */}
            <aside className="col-span-4 lg:col-span-3">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden sticky top-4 shadow-sm">
                <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-50">
                  Sections ({filtered.length})
                </div>
                <div className="max-h-[calc(100vh-220px)] overflow-y-auto divide-y divide-slate-100">
                  {filtered.map((s) => (
                    <button
                      key={s.id}
                      data-testid={`ess-user-guide-toc-${s.id}`}
                      onClick={() => setActiveId(s.id)}
                      className={`w-full text-left px-4 py-2.5 text-xs flex items-center justify-between transition ${
                        activeId === s.id
                          ? 'bg-blue-50 text-blue-700 font-bold border-l-4 border-blue-600'
                          : 'text-slate-700 hover:bg-slate-50 font-medium'
                      }`}
                    >
                      <span className="truncate pr-2">{s.title}</span>
                      <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${activeId === s.id ? 'text-blue-600' : 'text-slate-300'}`} />
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <div className="px-4 py-6 text-xs text-slate-400 text-center">No sections match your search.</div>
                  )}
                </div>
              </div>
            </aside>

            {/* Content */}
            <section className="col-span-8 lg:col-span-9">
              {!active ? (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
                  Select a section on the left to view instructions.
                </div>
              ) : (
                <article
                  data-testid={`ess-user-guide-section-${active.id}`}
                  className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm"
                >
                  <h2 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-blue-600 shrink-0" />
                    {active.title}
                  </h2>
                  <p className="text-sm text-slate-700 leading-relaxed mb-6 bg-slate-50 border border-slate-100 rounded-lg p-3">
                    {active.intro}
                  </p>

                  {active.steps && active.steps.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-blue-600" />
                        Step-by-step Instructions
                      </h3>
                      <div className="space-y-2.5">
                        {active.steps.map((s, i) => (
                          <div key={i} className="flex items-start gap-3 bg-white border border-slate-100 rounded-lg p-3 text-sm text-slate-700 hover:border-slate-200 transition-colors">
                            <span className="shrink-0 w-6 h-6 rounded-full bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-center">
                              {i + 1}
                            </span>
                            <div className="leading-relaxed pt-0.5">{s}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {active.faqs && active.faqs.length > 0 && (
                    <div className="mb-6 space-y-3">
                      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5">
                        <HelpCircle className="w-4 h-4 text-emerald-600" />
                        Frequently Asked Questions
                      </h3>
                      {active.faqs.map((f, i) => (
                        <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-1.5">
                          <p className="text-sm font-bold text-slate-900">Q: {f.q}</p>
                          <p className="text-sm text-slate-700 leading-relaxed">A: {f.a}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {(active.tips || []).length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">
                        <Lightbulb className="w-4 h-4 text-amber-600" /> Tips &amp; Best Practices
                      </div>
                      <ul className="space-y-2 text-sm text-amber-900">
                        {active.tips.map((t, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-amber-600 font-bold">•</span>
                            <span className="leading-relaxed">{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </article>
              )}
            </section>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

